import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, distinctUntilChanged } from 'rxjs';

/** The startup spinner never stays up longer than this, whatever happens while the app loads. */
const MAX_SPINNER_MS = 5000;

/**
 * Full-screen spinner shown ONCE, while the app first loads. It is not tied to HTTP requests:
 * once hidden (by AppComponent.onLoopComplete() or the safety timer) it never shows again.
 */
@Injectable({ providedIn: 'root' })
export class LoadingSpinnerService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private initialLoadDone = false;
  private safetyTimer: ReturnType<typeof setTimeout> | null = null;
  private isLoading = new BehaviorSubject<boolean>(true);
  isLoading$ = this.isLoading.asObservable().pipe(distinctUntilChanged());

  constructor() {
    // Browser only: a pending timer on the server would delay server-side rendering.
    if (this.isBrowser) {
      this.safetyTimer = setTimeout(() => this.forceHide(), MAX_SPINNER_MS);
    }
  }

  /** Kept for existing callers; the spinner is not shown again after the first load. */
  show() { }

  hide() {
    this.forceHide();
  }

  forceHide() {
    if (this.initialLoadDone) return;
    this.initialLoadDone = true;
    if (this.safetyTimer) {
      clearTimeout(this.safetyTimer);
      this.safetyTimer = null;
    }
    this.isLoading.next(false);
  }
}
